/**
 * Block 13 PR-G Phase 2 Step 4a — drawing primitives для renderContractToPdf.
 *
 * Mirror Block 12 font + layout pattern. Содержит только pure / draw-helpers
 * без contract-specific orchestration:
 *
 *   - PT Sans font lazy-load + base64 cache (session-scoped).
 *   - LAYOUT / FONT_SIZES / COLORS constants.
 *   - ensureSpace pagination primitive (jsPDF addPage on overflow).
 *   - drawTitle / drawCenteredLine / drawParagraph / drawSectionHeading.
 *   - drawTwoColumnTable (для requisites).
 *   - **Phase 6.5F.3:** drawSpecBlocks — per-block tables (Название | Тип |
 *     Бренд) per Excel template от Aibek + block_cost row + multi-block
 *     pagination. **Replaces** legacy drawSpecTable + buildSpecDataRows
 *     (6-col flat layout dropped в A7 redesign).
 *   - drawSignatureFooter / drawPageNumbers.
 *   - `safe()` — normalizeApostrophes applied to every drawn string.
 *
 * Section orchestration (fillSectionParagraphs / composeRequisitesTable / main
 * renderContractToPdf) lives в `renderContractToPdf.ts`.
 *
 * Reference:
 *   - ADR-0025 §Q4 (PDF architecture, font verification amendment)
 *   - ADR-0025 Amendment A7.3 (Phase 6.5F.3 — per-block tables format)
 *   - ADR-0022 «Update 2026-05-23: pivot to jsPDF»
 *   - apps/web/src/features/calculator/quote/services/renderQuoteToPdf.ts
 *   - `template-spec-pdf.png` (Excel reference от Aibek)
 */

import type { jsPDF } from 'jspdf';
import type { ContractCurrencyCode, ContractSpecBlock } from '@pomoshnik/shared';
import { getCurrencyLabel } from '@pomoshnik/shared';

import { formatCurrencyDigits, normalizeApostrophes } from './contractTextRender';
import { isSpecRowVisibleInPdf } from './specRowFilters';

// ── Font setup ─────────────────────────────────────────────────────────────

export const FONT_NAME = 'PTSans';
export const FONT_FILES = {
  regular: {
    url: '/fonts/PTSans-Regular.ttf',
    vfs: 'PTSans-Regular.ttf',
    style: 'normal' as const,
  },
  bold: {
    url: '/fonts/PTSans-Bold.ttf',
    vfs: 'PTSans-Bold.ttf',
    style: 'bold' as const,
  },
} as const;

type FontKey = keyof typeof FONT_FILES;

const cachedFontBase64: Record<FontKey, string | null> = {
  regular: null,
  bold: null,
};

export async function loadFontBase64(key: FontKey): Promise<string> {
  const cached = cachedFontBase64[key];
  if (cached !== null) return cached;

  const file = FONT_FILES[key];
  const response = await fetch(file.url);
  if (!response.ok) {
    throw new Error(`Не удалось загрузить шрифт (${response.status} ${response.statusText})`);
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength < 1024 || !hasTrueTypeMagic(buffer)) {
    throw new Error(`Шрифт повреждён или не TTF (${buffer.byteLength} bytes)`);
  }
  const base64 = arrayBufferToBase64(buffer);
  cachedFontBase64[key] = base64;
  return base64;
}

function hasTrueTypeMagic(buffer: ArrayBuffer): boolean {
  const head = new Uint8Array(buffer, 0, 4);
  return head[0] === 0x00 && head[1] === 0x01 && head[2] === 0x00 && head[3] === 0x00;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

/** Reset cache — exposed для unit-test isolation. */
export function __resetFontCacheForTests(): void {
  cachedFontBase64.regular = null;
  cachedFontBase64.bold = null;
}

// ── Layout / sizing constants ──────────────────────────────────────────────

export const LAYOUT = {
  pageW: 210,
  pageH: 297,
  marginX: 20,
  marginTop: 20,
  marginBottom: 18,
  continuationTop: 20,
  paragraphGap: 3,
  sectionGap: 5,
  headingGapAfter: 3,
  tableRowMinH: 8,
  tableLineH: 5,
  pageNumberY: 290,
} as const;

export const FONT_SIZES = {
  title: 16,
  h2: 13,
  body: 11,
  tableCell: 10,
  pageNumber: 9,
} as const;

const COLORS = {
  ink: [0, 0, 0],
  faint: [180, 180, 180],
} as const;

type RgbTuple = readonly [number, number, number];

export interface RenderCursor {
  y: number;
}

function setDraw(pdf: jsPDF, rgb: RgbTuple): void {
  pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
}

function setTextColor(pdf: jsPDF, rgb: RgbTuple): void {
  pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
}

export function ensureSpace(pdf: jsPDF, cursor: RenderCursor, needed: number): void {
  if (cursor.y + needed > LAYOUT.pageH - LAYOUT.marginBottom) {
    pdf.addPage();
    cursor.y = LAYOUT.continuationTop;
  }
}

/**
 * Normalize ALL text before draw — user input может содержать typographic
 * apostrophes (U+02BB / U+02BC / U+2018 / U+2019), которые PT Sans не
 * поддерживает (Phase 2 Step 1 verification). Helper заменяет их на ASCII `'`.
 */
function safe(text: string): string {
  return normalizeApostrophes(text);
}

// ── Drawing primitives ─────────────────────────────────────────────────────

export function drawTitle(pdf: jsPDF, cursor: RenderCursor, title: string): void {
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(FONT_SIZES.title);
  setTextColor(pdf, COLORS.ink);
  const lines = pdf.splitTextToSize(safe(title), LAYOUT.pageW - 2 * LAYOUT.marginX) as string[];
  for (const line of lines) {
    ensureSpace(pdf, cursor, 8);
    pdf.text(line, LAYOUT.pageW / 2, cursor.y + 6, { align: 'center' });
    cursor.y += 8;
  }
  cursor.y += LAYOUT.headingGapAfter;
}

export function drawCenteredLine(
  pdf: jsPDF,
  cursor: RenderCursor,
  text: string,
  size: number,
): void {
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(size);
  setTextColor(pdf, COLORS.ink);
  ensureSpace(pdf, cursor, size * 0.5 + 2);
  pdf.text(safe(text), LAYOUT.pageW / 2, cursor.y + size * 0.4, { align: 'center' });
  cursor.y += size * 0.5 + 2;
}

export function drawParagraph(
  pdf: jsPDF,
  cursor: RenderCursor,
  text: string,
  options: { bold?: boolean; size?: number } = {},
): void {
  const size = options.size ?? FONT_SIZES.body;
  pdf.setFont(FONT_NAME, options.bold ? 'bold' : 'normal');
  pdf.setFontSize(size);
  setTextColor(pdf, COLORS.ink);
  const lines = pdf.splitTextToSize(safe(text), LAYOUT.pageW - 2 * LAYOUT.marginX) as string[];
  const lineH = size * 0.45 + 1;
  for (const line of lines) {
    ensureSpace(pdf, cursor, lineH);
    pdf.text(line, LAYOUT.marginX, cursor.y + lineH * 0.7);
    cursor.y += lineH;
  }
  cursor.y += LAYOUT.paragraphGap;
}

export function drawSectionHeading(
  pdf: jsPDF,
  cursor: RenderCursor,
  number: string,
  heading: string,
): void {
  ensureSpace(pdf, cursor, 14);
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(FONT_SIZES.h2);
  setTextColor(pdf, COLORS.ink);
  pdf.text(safe(`${number}. ${heading}`), LAYOUT.marginX, cursor.y + 6);
  cursor.y += 8 + LAYOUT.headingGapAfter;
}

export function drawTwoColumnTable(
  pdf: jsPDF,
  cursor: RenderCursor,
  rows: readonly (readonly string[])[],
  options: { headerRow?: boolean } = {},
): void {
  if (rows.length === 0) return;
  const tableW = LAYOUT.pageW - 2 * LAYOUT.marginX;
  const colW = tableW / 2;
  const xLeft = LAYOUT.marginX;
  const xRight = LAYOUT.marginX + colW;

  pdf.setFontSize(FONT_SIZES.tableCell);
  setDraw(pdf, COLORS.faint);
  pdf.setLineWidth(0.2);

  for (const [idx, row] of rows.entries()) {
    const isHeader = options.headerRow && idx === 0;
    pdf.setFont(FONT_NAME, isHeader ? 'bold' : 'normal');
    const leftCell = safe(row[0] ?? '');
    const rightCell = safe(row[1] ?? '');
    const leftLines = pdf.splitTextToSize(leftCell, colW - 4) as string[];
    const rightLines = pdf.splitTextToSize(rightCell, colW - 4) as string[];
    const lineCount = Math.max(leftLines.length, rightLines.length, 1);
    const rowH = Math.max(LAYOUT.tableRowMinH, lineCount * LAYOUT.tableLineH + 2);

    ensureSpace(pdf, cursor, rowH);

    leftLines.forEach((line, i) => pdf.text(line, xLeft + 2, cursor.y + 5 + i * LAYOUT.tableLineH));
    rightLines.forEach((line, i) =>
      pdf.text(line, xRight + 2, cursor.y + 5 + i * LAYOUT.tableLineH),
    );

    pdf.line(xLeft, cursor.y + rowH, xLeft + tableW, cursor.y + rowH);
    pdf.line(xRight, cursor.y, xRight, cursor.y + rowH);
    cursor.y += rowH;
  }
  setTextColor(pdf, COLORS.ink);
}

// ── Spec blocks rendering (Phase 6.5F.3 — A7 redesign) ────────────────────

/**
 * Phase 6.5F.3 — labels for spec block table per Aibek Excel template
 * (`template-spec-pdf.png`). Hardcoded in render (independent of template
 * JSON) per A7 redesign — template.spec.columns больше не используется.
 */
const SPEC_HEADER_LABELS = ['Название', 'Тип', 'Бренд'] as const;
const SPEC_COST_ROW_LABEL = 'Стоимость';

/** 3-col fractional widths: Название 50% / Тип 25% / Бренд 25%. */
const SPEC_COL_FRACTIONS = [0.5, 0.25, 0.25] as const;

const SPEC_CELL_PAD = 2;

/**
 * Phase 6.5F.3 — render per-block spec tables (replaces legacy 6-col
 * `drawSpecTable` + `buildSpecDataRows`).
 *
 * **Layout per block** (per `template-spec-pdf.png`):
 *   1. Block title row — bold, full-width, centered (e.g. «Кухня»).
 *   2. Header row — 3 cols bold: «Название | Тип | Бренд».
 *   3. Body rows — для каждой row где `key !== 'block_cost'`:
 *      `[label, type ?? '', brand ?? '']`. Empty cells render blank.
 *   4. Cost row — для block_cost row: `[Стоимость, '', formatted cost]`,
 *      bold. Rendered last (Excel convention).
 *
 * **Skip rule (body rows):** rows where label AND type AND brand all empty/
 * whitespace — skipped (UX trap protection, mirror legacy `buildSpecDataRows`
 * pattern + costCustomRows skip behaviour).
 *
 * **Multi-block:** vertical `paragraphGap` between blocks. Per-row pagination
 * via `ensureSpace`.
 *
 * **Empty blocks list:** caller handles fallback (currently `renderContractToPdf`
 * renders `template.spec.defaultRows` as paragraphs when `draft.spec` empty).
 */
export function drawSpecBlocks(
  pdf: jsPDF,
  cursor: RenderCursor,
  blocks: readonly ContractSpecBlock[],
  currencyCode: ContractCurrencyCode,
): void {
  if (blocks.length === 0) return;
  const tableW = LAYOUT.pageW - 2 * LAYOUT.marginX;
  const colWidths = SPEC_COL_FRACTIONS.map((f) => tableW * f);

  setDraw(pdf, COLORS.faint);
  pdf.setLineWidth(0.2);
  pdf.setFontSize(FONT_SIZES.tableCell);

  blocks.forEach((block, blockIdx) => {
    drawBlockTitleRow(pdf, cursor, block.title, tableW);
    drawSpecHeaderRow(pdf, cursor, colWidths);

    // Body rows: всё кроме block_cost. PR-C4 round-2 (ADR-0038) —
    // `isSpecRowVisibleInPdf` = непустой label ИЛИ isSpecRowFilled (Тип/Бренд/
    // cost). PDF **зеркалит форму** (`SpecBlockEditor` показывает все customRows):
    // ручные шаблонные позиции (Столешница, Фасады…) видны даже без Тип/Бренд.
    // Прячутся только полностью пустые строки. Round-5 (A8.6) отменён Aibek.
    for (const row of block.rows) {
      if (row.key === 'block_cost') continue;
      if (!isSpecRowVisibleInPdf(row)) continue;
      drawSpecBodyRow(pdf, cursor, colWidths, [row.label, row.type ?? '', row.brand ?? '']);
    }

    // Cost rows (обычно одна block_cost row per block). Rendered last (Excel
    // convention). Cost format = formatCurrencyDigits + currency label
    // («500 000 тенге») per Phase 7 hotfix round 4 (R4-#2, Aibek visual verify
    // round 9 — «без валюты» fix). Пустая cost (undefined ИЛИ 0) → пустая cell
    // (preserved behaviour — не printим «0 тенге»).
    for (const row of block.rows) {
      if (row.key !== 'block_cost') continue;
      const costCell =
        row.cost !== undefined && row.cost > 0
          ? `${formatCurrencyDigits(row.cost)} ${getCurrencyLabel(currencyCode)}`
          : '';
      drawSpecCostRow(pdf, cursor, colWidths, costCell);
    }

    // Gap между blocks (но не после последнего).
    if (blockIdx < blocks.length - 1) {
      cursor.y += LAYOUT.paragraphGap;
    }
  });

  setTextColor(pdf, COLORS.ink);
}

function drawBlockTitleRow(pdf: jsPDF, cursor: RenderCursor, title: string, tableW: number): void {
  const rowH = Math.max(LAYOUT.tableRowMinH, LAYOUT.tableRowMinH + 2);
  ensureSpace(pdf, cursor, rowH);
  pdf.setFont(FONT_NAME, 'bold');
  setTextColor(pdf, COLORS.ink);
  const safeTitle = safe(title);
  pdf.text(safeTitle, LAYOUT.pageW / 2, cursor.y + rowH * 0.65, { align: 'center' });
  // Border rectangle around title row (full width).
  pdf.line(LAYOUT.marginX, cursor.y, LAYOUT.marginX + tableW, cursor.y);
  pdf.line(LAYOUT.marginX, cursor.y, LAYOUT.marginX, cursor.y + rowH);
  pdf.line(LAYOUT.marginX + tableW, cursor.y, LAYOUT.marginX + tableW, cursor.y + rowH);
  pdf.line(LAYOUT.marginX, cursor.y + rowH, LAYOUT.marginX + tableW, cursor.y + rowH);
  cursor.y += rowH;
}

function drawSpecHeaderRow(pdf: jsPDF, cursor: RenderCursor, colWidths: readonly number[]): void {
  ensureSpace(pdf, cursor, LAYOUT.tableRowMinH);
  pdf.setFont(FONT_NAME, 'bold');
  setTextColor(pdf, COLORS.ink);

  let x = LAYOUT.marginX;
  const rowH = LAYOUT.tableRowMinH;
  for (let i = 0; i < SPEC_HEADER_LABELS.length; i++) {
    const cellW = colWidths[i]!;
    pdf.text(safe(SPEC_HEADER_LABELS[i]!), x + SPEC_CELL_PAD, cursor.y + rowH * 0.65);
    pdf.line(x, cursor.y, x, cursor.y + rowH);
    x += cellW;
  }
  // Right-edge vertical line + top/bottom horizontal lines.
  pdf.line(x, cursor.y, x, cursor.y + rowH);
  pdf.line(LAYOUT.marginX, cursor.y, x, cursor.y);
  pdf.line(LAYOUT.marginX, cursor.y + rowH, x, cursor.y + rowH);
  cursor.y += rowH;
}

function drawSpecBodyRow(
  pdf: jsPDF,
  cursor: RenderCursor,
  colWidths: readonly number[],
  cells: readonly [string, string, string],
): void {
  pdf.setFont(FONT_NAME, 'normal');
  setTextColor(pdf, COLORS.ink);

  const cellLines = cells.map(
    (c, i) => pdf.splitTextToSize(safe(c), colWidths[i]! - 2 * SPEC_CELL_PAD) as string[],
  );
  const lineCount = Math.max(...cellLines.map((l) => l.length), 1);
  const rowH = Math.max(LAYOUT.tableRowMinH, lineCount * LAYOUT.tableLineH + 2);
  ensureSpace(pdf, cursor, rowH);

  let x = LAYOUT.marginX;
  cellLines.forEach((lines, i) => {
    const cellW = colWidths[i]!;
    lines.forEach((line, li) =>
      pdf.text(line, x + SPEC_CELL_PAD, cursor.y + 5 + li * LAYOUT.tableLineH),
    );
    pdf.line(x, cursor.y, x, cursor.y + rowH);
    x += cellW;
  });
  pdf.line(x, cursor.y, x, cursor.y + rowH);
  pdf.line(LAYOUT.marginX, cursor.y + rowH, x, cursor.y + rowH);
  cursor.y += rowH;
}

function drawSpecCostRow(
  pdf: jsPDF,
  cursor: RenderCursor,
  colWidths: readonly number[],
  costCell: string,
): void {
  pdf.setFont(FONT_NAME, 'bold');
  setTextColor(pdf, COLORS.ink);

  const rowH = LAYOUT.tableRowMinH;
  ensureSpace(pdf, cursor, rowH);

  const cells: readonly [string, string, string] = [SPEC_COST_ROW_LABEL, '', costCell];
  let x = LAYOUT.marginX;
  for (let i = 0; i < cells.length; i++) {
    const cellW = colWidths[i]!;
    const text = cells[i]!;
    if (text !== '') {
      pdf.text(safe(text), x + SPEC_CELL_PAD, cursor.y + rowH * 0.65);
    }
    pdf.line(x, cursor.y, x, cursor.y + rowH);
    x += cellW;
  }
  pdf.line(x, cursor.y, x, cursor.y + rowH);
  pdf.line(LAYOUT.marginX, cursor.y + rowH, x, cursor.y + rowH);
  cursor.y += rowH;
}

export function drawSignatureFooter(pdf: jsPDF, cursor: RenderCursor, footer: string): void {
  cursor.y += LAYOUT.sectionGap;
  drawParagraph(pdf, cursor, footer, { bold: true });
}

export function drawPageNumbers(pdf: jsPDF): void {
  const totalPages = pdf.getNumberOfPages();
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(FONT_SIZES.pageNumber);
  setTextColor(pdf, COLORS.faint);
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.text(`Стр. ${i} из ${totalPages}`, LAYOUT.pageW / 2, LAYOUT.pageNumberY, {
      align: 'center',
    });
  }
  setTextColor(pdf, COLORS.ink);
}
